import { useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { DocumentIntelligencePanel } from "@/components/DocumentIntelligencePanel";
import { Upload, FileText, X, Loader2, CheckCircle2, AlertCircle, Image, Plus } from "lucide-react";
import type { FieldConfig } from "./DynamicField";
import type { NormalizedDocumentIntelligence } from "@/lib/document-intelligence";
import { parseDealDocument, uploadDealDocument } from "@/lib/deal-document-upload";

const ACCEPTED_TYPES = ".pdf,.csv,.xls,.xlsx,.jpg,.jpeg,.png";
const ACCEPTED_MIMES = [
  "application/pdf",
  "text/csv",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg",
  "image/png",
];
const MAX_SIZE = 20 * 1024 * 1024; // 20MB

interface FileUploadFieldProps {
  field: FieldConfig;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  error?: string;
  dealId?: string | null;
}

interface FileValue {
  document_id: string;
  file_name: string;
  status: string;
  extracted_data?: Record<string, unknown> | null;
  intelligence?: NormalizedDocumentIntelligence | null;
}

// Normalize incoming value (legacy single-object) to array
function toFileArray(value: unknown): FileValue[] {
  if (!value) return [];
  if (Array.isArray(value)) return value as FileValue[];
  if (typeof value === "object" && (value as any).document_id) return [value as FileValue];
  return [];
}

export function FileUploadField({ field, value, onChange, error, dealId }: FileUploadFieldProps) {
  const files = toFileArray(value);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadingLabel, setUploadingLabel] = useState<"uploading" | "parsing">("uploading");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = useCallback(async (incoming: File[]) => {
    setErrorMsg(null);
    if (incoming.length === 0) return;

    for (const file of incoming) {
      if (!ACCEPTED_MIMES.includes(file.type)) {
        setErrorMsg(`"${file.name}": unsupported file type. Use PDF, CSV, Excel, JPG, or PNG.`);
        return;
      }
      if (file.size > MAX_SIZE) {
        setErrorMsg(`"${file.name}" exceeds 20MB limit.`);
        return;
      }
    }
    if (!dealId) {
      setErrorMsg("Please save the deal first (click Next on any step).");
      return;
    }

    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      setErrorMsg("You must be logged in.");
      return;
    }

    setIsUploading(true);
    const userId = session.user.id;
    let current = [...files];

    try {
      for (let i = 0; i < incoming.length; i++) {
        const file = incoming[i];
        setUploadingLabel("uploading");
        setProgress(Math.round(((i + 0.3) / incoming.length) * 100));

        const uploadResult = await uploadDealDocument({
          dealId,
          docType: field.field_key,
          file,
          userId,
        });

        const result: FileValue = {
          document_id: uploadResult.documentId,
          file_name: file.name,
          status: "uploaded",
        };

        if (field.scan_template_id) {
          setUploadingLabel("parsing");
          setProgress(Math.round(((i + 0.7) / incoming.length) * 100));
          try {
            const parsed = await parseDealDocument({
              documentId: uploadResult.documentId,
              docType: field.field_key,
              scanTemplateId: field.scan_template_id,
            });
            result.extracted_data = parsed.extracted_data ?? undefined;
            result.intelligence = parsed.intelligence ?? undefined;
            result.status = parsed.intelligence || parsed.extracted_data ? "parsed" : "uploaded";
          } catch (parseError) {
            console.error("Document parse error:", parseError);
            result.status = "uploaded_parse_failed";
          }
        }

        current = [...current, result];
        onChange(field.field_key, current);
      }

      setProgress(100);
    } catch (err: any) {
      setErrorMsg(err.message || "Upload failed");
    } finally {
      setIsUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [dealId, field.field_key, field.scan_template_id, files, onChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const dropped = Array.from(e.dataTransfer.files || []);
    if (dropped.length) handleFiles(dropped);
  }, [handleFiles]);

  const handleRemove = async (idx: number) => {
    const target = files[idx];
    const confirmed = window.confirm(
      `Remove "${target?.file_name ?? "this file"}"?`
    );
    if (!confirmed) return;
    if (target?.document_id) {
      await supabase.from("deal_documents").delete().eq("id", target.document_id);
    }
    const next = files.filter((_, i) => i !== idx);
    onChange(field.field_key, next.length ? next : null);
  };

  const renderLabel = () => (
    <div className="flex items-center gap-1 mb-1.5">
      <Label htmlFor={field.field_key} className="text-sm font-medium text-foreground">
        {field.label}
      </Label>
      {field.is_required && <span className="text-destructive text-xs">*</span>}
      {files.length > 0 && (
        <span className="ml-2 text-2xs text-muted-foreground">
          {files.length} file{files.length === 1 ? "" : "s"} uploaded
        </span>
      )}
    </div>
  );

  const helperText = field.helper_text || (
    field.scan_template_id
      ? "Upload one or more documents. You can add as many supporting files as needed (T12s, rent rolls, addendums, etc.)."
      : null
  );

  const renderFileExtracted = (fv: FileValue) => {
    const intelligence = fv.intelligence;
    if (!fv.extracted_data || typeof fv.extracted_data !== "object") {
      return intelligence ? (
        <div className="mt-3">
          <DocumentIntelligencePanel intelligence={intelligence} mode="compact" />
        </div>
      ) : null;
    }
    if (intelligence) {
      return (
        <div className="mt-3 space-y-3 rounded-md border border-border bg-muted/50 p-3">
          <DocumentIntelligencePanel intelligence={intelligence} mode="compact" />
        </div>
      );
    }
    const entries = Object.entries(fv.extracted_data).filter(
      ([, v]) => v != null && v !== "" && typeof v !== "object"
    ).slice(0, 8);
    if (entries.length === 0) return null;
    return (
      <div className="mt-3 rounded-md border border-border bg-muted/50 p-3">
        <p className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
          Extracted Data
        </p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          {entries.map(([key, val]) => (
            <div key={key} className="flex justify-between text-xs">
              <span className="text-muted-foreground truncate mr-2">{key.replace(/_/g, " ")}</span>
              <span className="font-medium text-foreground">
                {typeof val === "number" ? val.toLocaleString() : String(val)}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div>
      {renderLabel()}

      {/* File list */}
      {files.length > 0 && (
        <div className="space-y-2 mb-3">
          {files.map((fv, idx) => {
            const isImage = fv.file_name?.match(/\.(jpg|jpeg|png)$/i);
            return (
              <div key={`${fv.document_id}-${idx}`} className="rounded-lg border border-border bg-card p-3">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                    {isImage ? <Image className="h-4 w-4 text-primary" /> : <FileText className="h-4 w-4 text-primary" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{fv.file_name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      {fv.status === "parsed" ? (
                        <><CheckCircle2 className="h-3 w-3 text-green-600" /><span className="text-2xs text-green-600">Parsed successfully</span></>
                      ) : fv.status === "uploaded_parse_failed" ? (
                        <><AlertCircle className="h-3 w-3 text-amber-500" /><span className="text-2xs text-amber-500">Uploaded (parsing failed)</span></>
                      ) : (
                        <><CheckCircle2 className="h-3 w-3 text-primary" /><span className="text-2xs text-muted-foreground">Uploaded</span></>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => handleRemove(idx)}
                    disabled={isUploading}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                {renderFileExtracted(fv)}
              </div>
            );
          })}
        </div>
      )}

      {/* Uploader */}
      {isUploading ? (
        <div className="rounded-lg border-2 border-dashed border-primary/30 bg-primary/5 p-6 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
          <p className="text-sm font-medium text-foreground">
            {uploadingLabel === "parsing" ? "Analyzing document..." : "Uploading..."}
          </p>
          <Progress value={progress} className="h-1.5 mt-3 max-w-xs mx-auto" />
        </div>
      ) : (
        <div
          className={`rounded-lg border-2 border-dashed p-6 text-center transition-colors cursor-pointer ${
            dragging
              ? "border-primary bg-primary/5"
              : errorMsg
              ? "border-destructive/50 bg-destructive/5"
              : "border-input hover:border-primary/50 hover:bg-muted/30"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_TYPES}
            multiple
            className="hidden"
            onChange={(e) => {
              const selected = Array.from(e.target.files || []);
              if (selected.length) handleFiles(selected);
            }}
          />
          {files.length > 0 ? (
            <>
              <Plus className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground">
                {dragging ? "Drop files here" : "Add more files"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Drag & drop or click — multiple files supported
              </p>
            </>
          ) : (
            <>
              <Upload className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm font-medium text-foreground">
                {dragging ? "Drop files here" : "Drag & drop or click to browse"}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                PDF, CSV, Excel, JPG, PNG — Max 20MB each — multiple files supported
              </p>
            </>
          )}
        </div>
      )}

      {errorMsg && <p className="text-xs text-destructive mt-1">{errorMsg}</p>}
      {error && <p className="text-xs text-destructive mt-1">{error}</p>}
      {helperText && <p className="text-xs text-muted-foreground mt-1">{helperText}</p>}
    </div>
  );
}
